import { useRef, useState } from 'react';
import { Loader2, Share2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/shared/widgets/dialog';
import { WishlistIcon } from '@/shared/icons/icons';
import discountIcon from '@/shared/icons/discount.svg';
import { useWishlistToggle } from '@/features/wishlist/presentation/hooks/useWishlistToggle';
import { useGetUserContextQuery } from '@/shared/features/user-context/infra/adapters/userContextApiSlice';
import type { ListingDesignItem } from '../../domain/models/ProductListing';
import { ProductCardMediaCarousel } from './ProductCardMediaCarousel';
import { RotatingProductDescription } from './RotatingProductDescription';

interface ProductCardQuickPreviewProps {
  product: ListingDesignItem;
  productPosition: number;
  onClose: () => void;
  onShare: () => void;
  onNavigate: () => void;
  onWishlistClick?: (product: ListingDesignItem, position: number) => void;
  onWishlistToggle?: (product: ListingDesignItem, action: 'add' | 'remove') => void;
}

export function ProductCardQuickPreview({ product, productPosition, onClose, onShare, onNavigate,
  onWishlistClick, onWishlistToggle }: ProductCardQuickPreviewProps) {
  const { isWishlisted, toggleWishlist, isLoading } = useWishlistToggle();
  const { data: userContext } = useGetUserContextQuery(undefined);
  const pending = useRef(false);
  const [error, setError] = useState('');
  const wishlisted = isWishlisted(product.designId);
  const toggle = async () => {
    if (pending.current) return;
    pending.current = true;
    setError('');
    onWishlistClick?.(product, productPosition);
    // Release this modal's focus trap before the existing hook opens login.
    const loggedIn = (userContext as Record<string, unknown> | undefined)?.isLoggedIn === true;
    if (!loggedIn) onClose();
    try {
      const success = await toggleWishlist(product.designId);
      if (success) onWishlistToggle?.(product, wishlisted ? 'remove' : 'add');
      else if (loggedIn) setError('Unable to update wishlist. Please try again.');
    } finally { pending.current = false; }
  };
  return <Dialog open onOpenChange={open => { if (!open) onClose(); }}>
    <DialogContent aria-describedby={undefined} onCloseAutoFocus={event => event.preventDefault()}
      className="w-[min(82vw,360px)] max-h-[90dvh] gap-3 overflow-y-auto rounded-xl border-0 bg-transparent shadow-none sm:rounded-xl">
      <div className="select-none overflow-hidden rounded-xl bg-white text-[#103064]">
        <div className="relative aspect-square bg-[#F0F0F0]">
          {product.carouselMedia?.length ? (
            <ProductCardMediaCarousel designId={product.designId} designName={product.designName}
              media={product.carouselMedia} isActive autoSlide={false} showProgress onProductClick={onNavigate} />
          ) : <button type="button" aria-label={`Open ${product.designName}`} onClick={onNavigate} disabled={!product.productPageUrl} className="h-full w-full">
            {product.imageUrl ? <img src={product.imageUrl} alt={product.designName} className="h-full w-full object-contain" /> : <span>No image</span>}
          </button>}
          <button type="button" aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'} disabled={isLoading}
            onClick={toggle} className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#103064] disabled:opacity-50">
            {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <WishlistIcon filled={wishlisted} className={`h-5 w-5 ${wishlisted ? "text-[#B98599]" : "text-[#103064]"}`} />}
          </button>
        </div>
        <div className="p-3">
          <DialogTitle className="text-sm font-normal text-[#999]"><button type="button" onClick={onNavigate} disabled={!product.productPageUrl} className="text-left">{product.designName}</button></DialogTitle>
          <p className="mt-1 text-lg font-semibold">{product.discountedPrice || product.price}</p>
          <RotatingProductDescription product={product} className="flex h-8 items-center overflow-hidden text-xs leading-4 text-[#595959]" />
          {product.discountName && <p className="flex min-w-0 items-center gap-1 text-xs text-[#00852C]"><img src={discountIcon} alt="" className="h-4 w-4 shrink-0" /><span className="overflow-hidden whitespace-nowrap">{product.discountName}</span></p>}
          {error && <p role="alert" className="mt-2 text-xs text-red-600">{error}</p>}
        </div>
      </div>
      <button type="button" onClick={onShare} disabled={!product.productPageUrl} className="flex items-center justify-center gap-2 rounded-xl border border-[#CBD6E8] bg-[#EDF7FF] p-3 text-sm text-[#103064] disabled:opacity-50"><Share2 className="h-4 w-4" />Share</button>
    </DialogContent>
  </Dialog>;
}
